// ---------------------------------------------------------------------------
// Metric stats classifier
//
// Turns a raw metric score into a display classification + scale fraction using
// the metric's *historical distribution* (min/max/percentiles/direction)
// instead of the hard-coded heuristics in `sigsParser.classifyScore`.
//
// When no historical statistics are available for a metric, the resolver
// helpers fall back to the legacy heuristics so behaviour is unchanged.
// ---------------------------------------------------------------------------

import type { ScoreClass } from '../types/debug.js';
import type { EvalStats } from '../types/evalStats.js';

import { classifyScore } from './sigsParser.js';

/** Sentinel score meaning "not evaluated". */
const NOT_APPLICABLE_SCORE = -1;

/** Tercile cut-points (in percentile-rank space) for good / fair / poor. */
const FAIR_THRESHOLD = 100 / 3;
const GOOD_THRESHOLD = (100 / 3) * 2;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * How to resolve the ambiguous rank of a score that lands on a *point mass* — a
 * value shared by several percentile anchors (e.g. when 95% of samples are 0).
 * `upper` credits the top of the flat region (fraction of samples ≤ score);
 * `lower` credits the bottom (fraction strictly < score).
 */
type TieBreak = 'upper' | 'lower';

/**
 * A distinct distribution value and the percentile range it occupies. A *point
 * mass* — a value shared by several percentile samples (e.g. `5` when both p50
 * and p95 are 5) — spans `[minPercentile, maxPercentile]`; an ordinary value
 * has the two equal.
 */
interface Anchor {
  value: number;
  minPercentile: number;
  maxPercentile: number;
}

/**
 * Builds the monotonic (value-ascending) list of distinct-value anchors used for
 * interpolation: the min/max bounds plus every supplied percentile sample.
 * Values shared by several samples (a point mass) are merged into a single
 * anchor that records the full `[minPercentile, maxPercentile]` span, so
 * `percentileRankOf` can interpolate against the correct edge — the *upper* edge
 * when approaching from below and the *lower* edge of the value above — instead
 * of collapsing to one percentile and losing the other edge.
 */
function buildAnchors(stats: EvalStats): Anchor[] {
  // The 0/100 anchors bound the distribution. Prefer observed min/max when the
  // source supplies them; otherwise fall back to the theoretical scale. Either
  // way, widen to cover the percentile samples so the mapping stays monotonic
  // when a percentile sits outside the bounds (e.g. a `-1` "not evaluated"
  // sentinel below a scale that starts at 1).
  const percentileValues = stats.percentiles.map((p) => p.value);
  const lowBound = Math.min(stats.min ?? stats.scaleMin, ...percentileValues);
  const highBound = Math.max(stats.max ?? stats.scaleMax, ...percentileValues);

  const points = [
    { value: lowBound, percentile: 0 },
    ...stats.percentiles.map((p) => ({
      value: p.value,
      percentile: p.percentile,
    })),
    { value: highBound, percentile: 100 },
  ].sort((a, b) => a.value - b.value || a.percentile - b.percentile);

  const anchors: Anchor[] = [];
  for (const point of points) {
    const last = anchors[anchors.length - 1];
    if (last?.value === point.value) {
      // Point mass: widen the existing anchor's percentile span rather than
      // discarding an edge. The lower edge is needed to interpolate scores
      // approaching the mass from below; the upper edge to rank scores on it.
      last.minPercentile = Math.min(last.minPercentile, point.percentile);
      last.maxPercentile = Math.max(last.maxPercentile, point.percentile);
    } else {
      anchors.push({
        value: point.value,
        minPercentile: point.percentile,
        maxPercentile: point.percentile,
      });
    }
  }
  return anchors;
}

/**
 * Returns the percentile rank (0–100) of `score` within the metric's historical
 * distribution, linearly interpolating between the known percentile anchors.
 *
 * The quantile function is flat across a point mass and rises between distinct
 * values, so a score strictly between two values interpolates from the *upper*
 * edge of the lower value to the *lower* edge of the higher value. `tieBreak`
 * controls only how a score sitting exactly on a point mass is ranked: `upper`
 * credits the top of the flat region, `lower` the bottom (defaults to `upper`).
 */
export function percentileRankOf(
  score: number,
  stats: EvalStats,
  tieBreak: TieBreak = 'upper',
): number {
  const anchors = buildAnchors(stats);

  const first = anchors[0];
  const last = anchors[anchors.length - 1];
  if (first === undefined || last === undefined) return 0;

  // Percentile to credit a score sitting exactly on a value: the point mass's
  // top edge for `upper`, its bottom edge for `lower`.
  const atValue = (a: Anchor): number =>
    tieBreak === 'upper' ? a.maxPercentile : a.minPercentile;

  if (anchors.length === 1) {
    // Degenerate distribution (min === max). Below the value ranks at the
    // bottom; at/above it resolves by tie-break.
    return score < first.value ? first.minPercentile : atValue(first);
  }

  if (score < first.value) return first.minPercentile;
  if (score > last.value) return last.maxPercentile;

  for (let i = 0; i < anchors.length; i++) {
    const lo = anchors[i];
    if (lo === undefined) continue;
    if (score === lo.value) return atValue(lo);

    const hi = anchors[i + 1];
    if (hi !== undefined && score > lo.value && score < hi.value) {
      // Strictly between two distinct values: interpolate from the upper edge of
      // the lower value to the lower edge of the higher value.
      const t = (score - lo.value) / (hi.value - lo.value);
      return lo.maxPercentile + t * (hi.minPercentile - lo.maxPercentile);
    }
  }

  return last.maxPercentile;
}

/**
 * "Goodness" rank in 0–100, where 100 is the best possible outcome for the
 * metric. For higher-is-better metrics this is the percentile rank (resolving
 * point masses at their top); for lower-is-better metrics it is the inverted
 * rank (resolving point masses at their bottom). Resolving the tie toward the
 * metric's *worse* end ensures a best-case score sitting on a large flat region
 * (e.g. 0 for a lower-is-better metric that is almost always 0) is credited as
 * good instead of being penalised for being typical.
 */
function goodnessOf(score: number, stats: EvalStats): number {
  if (stats.direction === 'lowerIsBetter') {
    return 100 - percentileRankOf(score, stats, 'lower');
  }
  return percentileRankOf(score, stats, 'upper');
}

/**
 * Classifies a score using the metric's historical distribution and its `kind`.
 *
 *  • `-1` → `not-applicable` (the existing "not evaluated" sentinel)
 *  • `binary` metrics → `pass` / `fail`
 *  • `count` metrics → `not-applicable` (they are contextual, not graded; the
 *    grid renders them as a plain number and groups them by `kind`, so this
 *    value is not used for colour)
 *  • `scaled` / `ratio` / `categorical` → percentile-rank the score, adjust for
 *    direction, and bucket into `good` / `fair` / `poor` terciles.
 */
export function classifyScoreWithStats(
  score: number,
  stats: EvalStats,
): ScoreClass {
  if (score === NOT_APPLICABLE_SCORE) return 'not-applicable';

  if (stats.kind === 'binary') {
    const good =
      stats.direction === 'lowerIsBetter' ? score < 0.5 : score >= 0.5;
    return good ? 'pass' : 'fail';
  }

  // Count metrics are contextual (e.g. number of claims), not graded, so they
  // are never ranked good/fair/poor — matching how the grid renders them.
  if (stats.kind === 'count') return 'not-applicable';

  const goodness = goodnessOf(score, stats);
  if (goodness >= GOOD_THRESHOLD) return 'good';
  if (goodness >= FAIR_THRESHOLD) return 'fair';
  return 'poor';
}

/**
 * Fraction (0–1) of the metric's *theoretical scale* the score occupies, for the
 * progress bar. Position is in `[scaleMin, scaleMax]` space (e.g. 0–1 for a ratio,
 * 1–5 for a rubric) so a ratio that never exceeds 0.16 in practice still reads
 * against a full 0–1 axis; colour conveys goodness.
 */
export function getScaleFraction(score: number, stats: EvalStats): number {
  const span = stats.scaleMax - stats.scaleMin;
  if (span <= 0) return 1;
  return clamp((score - stats.scaleMin) / span, 0, 1);
}

/**
 * Classifies a score, preferring historical statistics and falling back to the
 * legacy heuristics when none are available for the metric.
 */
export function resolveScoreClass(
  score: number,
  evalName: string,
  stats: EvalStats | undefined,
): ScoreClass {
  return stats
    ? classifyScoreWithStats(score, stats)
    : classifyScore(score, evalName);
}

/**
 * Resolves the progress-bar fill fraction, preferring historical statistics and
 * falling back to the legacy fixed 0–5 scale when none are available.
 */
export function resolveScaleFraction(
  score: number,
  stats: EvalStats | undefined,
): number {
  return stats ? getScaleFraction(score, stats) : clamp(score / 5, 0, 1);
}

/**
 * Resolves the denominator used when displaying a raw score (e.g. `4/5`),
 * preferring the metric's theoretical scale maximum and falling back to the
 * legacy `5`.
 */
export function resolveScoreDenominator(stats: EvalStats | undefined): number {
  return stats ? stats.scaleMax : 5;
}
